import { defineStore } from 'pinia'
import { wxLogin as performWechatAuth } from '@/interfaces/wechat'
import { carrierLogin as performCarrierAuth } from '@/interfaces/carrierAuth'
import { mobileLogin as performSmsAuth } from '@/interfaces/mobileAuth'
import { verifyAuthToken, updateUserGeolocation } from '@/interfaces/user'

export const useAuthManager = defineStore('auth', {
  state: () => ({
    authToken: localStorage.getItem('token') || null,
    userProfile: JSON.parse(localStorage.getItem('user') || 'null'),
    lastAuthenticationTime: localStorage.getItem('lastLoginTime') || null,
    isInitializationFinished: false
  }),
  getters: {
    safeUserProfile: (state) => state.userProfile || {},
    // 获取 userId（兼容不同字段名）
    userId: (state) => state.userProfile?.userId || state.userProfile?.id || null,
    hasAvatar: (state) => !!state.userProfile?.avatar,
    isAuthenticated: (state) => !!state.authToken && !state.isTokenExpired,
    hasMobile: (state) => !!(state.userProfile?.mobile || state.userProfile?.mobilePartial),
    isTokenExpired: (state) => {
      if (!state.lastAuthenticationTime) return true
      return Date.now() - new Date(state.lastAuthenticationTime).getTime() > 365 * 24 * 60 * 60 * 1000
    }
  },
  actions: {
    // 微信登录
    async performWechatAuthentication(code) {
      const { token, nickname, avatar, status, userId, id } = await performWechatAuth(code)

      // 0:禁用, 1:正常, 2:关闭
      if (status !== 1) {
        const statusMsg = status === 0 ? '禁用' : '关闭'
        return {
          success: false,
          status: status,
          message: `帐号已${statusMsg}，请联系平台`
        }
      }

      this.authToken = token
      this.userProfile = { userId: userId || id, nickname, avatar }
      this.lastAuthenticationTime = new Date().toISOString()

      // 使用localStorage持久化存储
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify({
        userId: userId || id,
        nickname,
        avatar,
      }))
      localStorage.setItem('lastLoginTime', this.lastAuthenticationTime)
      // 单独保存 userId
      if (userId || id) {
        localStorage.setItem('userId', String(userId || id));
      }

      // 更新用户服务地区信息
      this.updateServiceGeolocation()

      return { success: true }
    },

    // 运营商一键登录
    async performCarrierAuthentication(carrierToken, carrierType) {
      const result = await performCarrierAuth({
        carrierToken,
        carrierType
      })
      const { token, nickname, avatar, status, userId, id } = result

      // 0:禁用, 1:正常, 2:关闭
      if (status !== 1) {
        const statusMsg = status === 0 ? '禁用' : '关闭'
        return {
          success: false,
          status: status,
          message: `帐号已${statusMsg}，请联系平台`
        }
      }

      this.authToken = token
      this.userProfile = { userId: userId || id, nickname, avatar }
      this.lastAuthenticationTime = new Date().toISOString()

      // 使用localStorage持久化存储
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify({
        userId: userId || id,
        nickname,
        avatar,
      }))
      localStorage.setItem('lastLoginTime', this.lastAuthenticationTime)
      // 单独保存 userId
      if (userId || id) {
        localStorage.setItem('userId', String(userId || id));
      }

      // 更新用户服务地区信息
      this.updateServiceGeolocation()

      return { success: true }
    },

    // 手机号验证码登录
    async performSmsAuthentication(mobile, code) {
      try {
        const result = await performSmsAuth({ mobile, code });
        const { token, nickname, avatar, status, userId, id } = result;

        // 0:禁用, 1:正常, 2:关闭
        if (status !== 1) {
          const statusMsg = status === 0 ? '禁用' : '关闭';
          return {
            success: false,
            status: status,
            message: `帐号已${statusMsg}，请联系平台`
          };
        }

        // 保存手机号脱敏信息
        const mobilePartial = mobile.substring(0, 3) + "****" + mobile.substring(mobile.length - 4);

        this.authToken = token;
        // 保存完整的用户信息（包括 userId）
        this.userProfile = {
          userId: userId || id,
          nickname,
          avatar,
          mobilePartial
        };
        this.lastAuthenticationTime = new Date().toISOString();

        // 使用localStorage持久化存储
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(this.userProfile));
        localStorage.setItem('lastLoginTime', this.lastAuthenticationTime);
        // 单独保存 userId，方便快速访问
        if (this.userProfile.userId) {
          localStorage.setItem('userId', String(this.userProfile.userId));
        }

        // 更新用户服务地区信息
        this.updateServiceGeolocation()

        return { success: true };
      } catch (error) {
        // API 返回错误（如验证码过期、验证码错误等）
        console.error('手机号登录API错误:', error);
        return {
          success: false,
          isApiError: true,
          message: error.message || '登录失败，请重试'
        };
      }
    },

    // 初始化认证管理器
    async initialize() {
      try {
        if (this.authToken && !this.isTokenExpired) {
          const isValid = await verifyAuthToken(this.authToken)
          console.log("checkToken:", isValid);
          if (isValid != true) this.clearAuthentication()
        } else {
          this.clearAuthentication()
        }
      } catch (error) {
        console.error('Token验证失败:', error)
        this.clearAuthentication()
      } finally {
        this.isInitializationFinished = true
      }
    },
    // 清除认证信息
    clearAuthentication() {
      this.$reset()
      this.authToken = null
      this.userProfile = null
      this.lastAuthenticationTime = null
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('lastLoginTime')
      localStorage.removeItem('userId')
    },
    // 退出登录
    logout() {
      this.clearAuthentication()
    },

    // ========== 更新用户服务地区信息 ==========
    async updateServiceGeolocation() {
      try {
        const locationInfo = localStorage.getItem('user_location_info')
        if (locationInfo) {
          const parsed = JSON.parse(locationInfo)
          if (parsed.province || parsed.city || parsed.name) {
            await updateUserGeolocation({
              province: parsed.province || '',
              city: parsed.city || '',
              name: parsed.name || '',
              lng: parsed.longitude || 0,
              lat: parsed.latitude || 0
            })
            console.log('用户服务地区信息更新成功')
          }
        }
      } catch (error) {
        console.error('更新用户服务地区信息失败:', error)
        // 不阻塞登录流程，静默失败
      }
    },

    // ========== 新增：更新用户信息的 action ==========
    updateUserProfile(newInfo) {
      // 合并新信息到原始 userProfile（保持响应式）
      this.userProfile = {
        ...this.userProfile,
        ...newInfo
      };
      // 同步更新本地存储（与仓库状态一致）
      localStorage.setItem('user', JSON.stringify(this.userProfile));
      // 单独保存 userId，方便快速访问
      if (this.userProfile.userId) {
        localStorage.setItem('userId', String(this.userProfile.userId));
      }
    }
  }
})
